import {Button} from "../ui/button";
import {MultiSelectCombobox} from "../ui/comboBox";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import {Input} from "../ui/input";
import {Project} from "@/src/models/project";
import React, {useState, useContext, useMemo, useRef, useEffect} from "react";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import MapComponent from "../googleMaps/MapComponent";
import {
    Autocomplete,
} from "@react-google-maps/api";
import {handleAddressChanged} from "../googleMaps/handleChangheAddress";
import {moneyFormatter} from "../../lib/formatters";
import {Address} from "../../models/base";
import {useGoogleMapsAutocomplete} from "../../hooks/useGoogleMapsAutocomplete";
import {RichTextEditor, RichTextView} from "../ui/richtext";
import {USConstructionTypes} from "../../contexts/ConstructionType";
import {Spinner} from "../ui/spinner";
import {useParams} from "react-router-dom";
import {ProjectContext} from "../../contexts/ProjectContext";
import {CompanyType} from "../../models/company";


type NewProjectSetupProps = {
    methods: ReturnType<typeof useForm<Project>>;
    enabled: boolean;
    setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
    onSubmit: SubmitHandler<Project>;
};

export const NewProjectSetup: React.FC<NewProjectSetupProps> = (
    {
        methods,
        enabled,
        setEnabled,
        onSubmit,
    }) => {
    const {
        handleSubmit,
        formState: {errors},
    } = methods;
    const {project_id} = useParams<{project_id:string}>();
    const projectContext = useContext(ProjectContext);
    const [open, setOpen] = useState(false);
    const isCreatingNewProject = window.location.pathname === "/project/create"

    // editing is allowed IF
    //      (1) creating for the first time.
    //      (2) has teh developer role on the project
    useEffect(() => {
        if(projectContext?.role === CompanyType.builder){
            setEnabled(false);
        }
    }, [projectContext])



    const [location, setLocation] = useState<Address>({
        address: methods.getValues("address.address") || '',
        coordinates: {
            lat: methods.getValues("address.coordinates.lat") || 0,
            lon: methods.getValues("address.coordinates.lon") || 0,
        }
    });

    const updateLocation = (address: Address) => {
        methods.setValue('address.address', address.address);
        methods.setValue('address.coordinates', address.coordinates);
        setLocation(address);
    }

    const {autocompleteAddressRef, isLoaded, loadError} = useGoogleMapsAutocomplete();

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
        }
    };

    return (
        <div className="p-4 bg-white rounded shadow-md max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl mb-4">Project Information</h3>
                {/*{!isCreatingNewProject && userContext?.company_type === "developer" && (*/}
                  {!isCreatingNewProject && projectContext?.role === CompanyType.developer && (
                    <div className="mb-4 mr-5">
                        <label htmlFor="editing" className="mr-2 font-medium">
                            Editing:
                        </label>
                        <input
                            id="editing"
                            type="checkbox"
                            checked={enabled}
                            onChange={() => setEnabled(!enabled)}
                        />
                    </div>
                )}
            </div>

            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="grid grid-cols-2 gap-x-14 gap-y-4"
                >
                    <div className="col-span-1 grid gap-y-4 ">
                        <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                            <FormField
                                name="name"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>
                                            Project Name
                                            <span className="text-orange-500 ml-0.5">*</span>
                                        </FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Enter Project Name"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage className="text-xs">
                                            {errors.name?.message}
                                        </FormMessage>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name="number"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>Project Number</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Enter Project Number"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>{errors.number?.message}</FormMessage>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                            <FormField
                                name="sector"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>Sector</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Select sector"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>{errors.sector?.message}</FormMessage>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name="sub_sector"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>Sub-Sector</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Select Sub Sector"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>{errors.sub_sector?.message}</FormMessage>
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                            <FormField
                                name="gsf"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>GSF</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Enter GSF"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>{errors.gsf?.message}</FormMessage>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                name="ROM"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>ROM</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Enter ROM($)"
                                                    {...field}
                                                    value={field.value ? moneyFormatter.format(Number(field.value)) : ""}
                                                    onKeyDown={handleKeyDown}
                                                    onChange={(e) => {
                                                        const inputValue = e.target.value;
                                                        const rawValue = inputValue.replace(/[^0-9]/g, "");
                                                        field.onChange(rawValue);
                                                    }}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value ? moneyFormatter.format(field.value) : "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>{errors.gsf?.message}</FormMessage>
                                    </FormItem>
                                )}
                            />

                        </div>
                        <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                            <FormField
                                name="expected_duration"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>Expected Duration</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    placeholder="Enter Duration"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>
                                            {errors.expected_duration?.message}
                                        </FormMessage>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name="expected_start_date"
                                render={({field}) => (
                                    <FormItem>
                                        <FormLabel>Expected Start Date</FormLabel>
                                        <FormControl>
                                            {enabled ? (
                                                <Input
                                                    className="w-full"
                                                    type="date"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                />
                                            ) : (
                                                <div className="pt-3">
                                                    <span>{field.value || "N/A"}</span>
                                                </div>
                                            )}
                                        </FormControl>
                                        <FormMessage>
                                            {errors.expected_start_date?.message}
                                        </FormMessage>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            name="construction_types"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Construction Type(s)</FormLabel>
                                    <FormControl>
                                        {enabled ? (
                                            <MultiSelectCombobox
                                                types={
                                                    USConstructionTypes.map((x) => {
                                                        return {value: x, label: x}
                                                    })

                                                }
                                                open={open}
                                                setOpen={setOpen}
                                                value={Array.isArray(field.value)
                                                    ? field.value.filter((x) => USConstructionTypes.includes(x)) : []}
                                                onChange={field.onChange}
                                            />
                                        ) : (
                                            <div className="pt-2">
                        <span>
                          {Array.isArray(field.value) && field.value.length > 0
                              ? field.value.join(", ")
                              : "N/A"}
                        </span>
                                            </div>
                                        )}
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <FormField
                            name="stage"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Stage</FormLabel>
                                    <FormControl>
                                        {enabled ? (
                                            <Input
                                                className="w-full"
                                                placeholder="Enter Stage"
                                                {...field}
                                                value={field.value || ""}
                                                onKeyDown={handleKeyDown}
                                            />
                                        ) : (
                                            <div className="pt-3">
                                                <span>{field.value || "N/A"}</span>
                                            </div>
                                        )}
                                    </FormControl>
                                    <FormMessage>{errors.stage?.message}</FormMessage>
                                    <FormMessage>{errors.stage?.message}</FormMessage>
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="col-span-1">
                        <FormField
                            name="address.address"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel>Address</FormLabel>
                                    <FormControl>
                                        {enabled && isLoaded ? (
                                            <Autocomplete
                                                onLoad={(autocomplete) =>
                                                    (autocompleteAddressRef.current = autocomplete)
                                                }
                                                onPlaceChanged={() =>
                                                    handleAddressChanged(
                                                        field,
                                                        updateLocation,
                                                        autocompleteAddressRef
                                                    )
                                                }
                                            >
                                                <Input
                                                    className="w-full"
                                                    placeholder="Enter Address"
                                                    {...field}
                                                    value={field.value || ""}
                                                    onKeyDown={handleKeyDown}
                                                    onBlur={(e) => {
                                                        const newValue = e.target.value;
                                                        setLocation((prev) => ({
                                                            ...prev,
                                                            address: newValue,
                                                        }));

                                                    }}
                                                />
                                            </Autocomplete>
                                        ) : (
                                            <div className="mt-2">
                                                <span>{field.value || "N/A"}</span>
                                            </div>
                                        )}
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                        {/*address  */}
                        <div className="min-w-full mt-4 mb-4">
                            {!isLoaded ? (
                                <div>
                                    <Spinner className="size-16 text-gray-300">
                    <span className="text-lg text-gray-800 animate-pulse font-medium">
                    Map loading, please wait...
                    </span>
                                    </Spinner>
                                </div>
                            ) : (
                                <MapComponent
                                    userLocation={location}
                                    canSetLocation={enabled}
                                    setValue={methods.setValue}/>
                            )}
                            {loadError && <div>Error to load Map</div>}
                        </div>

                    </div>

                    <FormField
                        name="description"
                        render={({field}) => (
                            <FormItem className="col-span-2">
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                    {enabled ? (
                                        // <AutosizeTextarea
                                        //   className="w-full min-h-20"
                                        //   placeholder="Enter or upload Description"
                                        //   {...field}
                                        //   value={field.value || ""}
                                        // />
                                        <RichTextEditor className="w-full min-h-20"
                                                        initialValue={field.value as string}
                                                        onChange={(res: string) => methods.setValue("description", res)}
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <RichTextView initialValue={field.value || "N/A"}/>

                                        </div>
                                    )}
                                </FormControl>
                                <FormMessage>{errors.description?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                    {projectContext?.role === CompanyType.developer &&
                        <FormField
                        name="instructions"
                        render={({field}) => (
                            <FormItem className="col-span-2">
                                <FormLabel>Instructions To Bidders</FormLabel>
                                <FormControl>
                                    {enabled ? (
                                        <RichTextEditor
                                            className="w-full min-h-20"
                                            initialValue={field.value as string}
                                            onChange={(res: string) => methods.setValue("instructions", res)}
                                        />
                                    ) : (
                                        <div className="mt-2">
                                            <RichTextView initialValue={field.value || "N/A"}/>
                                        </div>
                                    )}
                                </FormControl>
                                <FormMessage>{errors.instructions?.message}</FormMessage>
                            </FormItem>
                        )}
                    />
                    }

                    {enabled && (
                        <div className="col-span-2 flex justify-end">
                            <Button type="submit" variant="outline">
                                Publish
                            </Button>
                        </div>
                    )}
                </form>
            </FormProvider>
        </div>
    );
};
